<script setup lang="ts">
  import { useFocusTrap } from '@tundr/ds-components';
  import { computed, onMounted, onUnmounted } from 'vue';
  import { useSlideoverStore } from '../../store/slideover.store';
  import {
    slideoverBackgroundClass,
    slideoverContainerClass,
    slideoverContainerVariantClass,
  } from './Slideover.css';
  import { SlideoverVariant } from './Slideover.types';

  const slideoverStore = useSlideoverStore();

  const focusTrap = useFocusTrap(
    slideoverStore.slideoverState.config?.firstSelectionOnLastItem,
  );

  function keydownListener(event: KeyboardEvent) {
    if (
      event.key === 'Escape' &&
      slideoverStore.isSlideoverOpen &&
      !slideoverStore.slideoverState.config?.blocking
    )
      slideoverStore.closeSlideover();
  }

  onMounted(() => {
    document.addEventListener('keydown', keydownListener);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', keydownListener);
  });

  const closeOnBackdrop = () => {
    if (!slideoverStore.slideoverState.config?.blocking)
      slideoverStore.closeSlideover();
  };

  const isVisible = computed<boolean>(() => {
    return !!slideoverStore.slideoverState.component;
  });

  const variantClass = computed<string>(() => {
    return slideoverContainerVariantClass[
      slideoverStore.slideoverState.config?.variant ?? SlideoverVariant.SM
    ];
  });
</script>

<template>
  <div>
    <Transition name="fade" appear>
      <div
        v-if="isVisible"
        :class="slideoverBackgroundClass"
        aria-hidden="true"
        @click.self="closeOnBackdrop"
      />
    </Transition>
    <Transition name="slide-in" appear>
      <div
        v-if="isVisible"
        :ref="focusTrap.trapRef"
        :class="[slideoverContainerClass, variantClass]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="slideover-title"
        tabindex="-1"
      >
        <component
          :is="slideoverStore.slideoverState.component"
          v-bind="slideoverStore.slideoverState.props"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
  .slide-in-enter-from,
  .slide-in-leave-to {
    transform: translateX(100%);
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    .slide-in-enter-active {
      transition: 0.3s ease-out all;
      transition-delay: 0.3s;
    }

    .slide-in-leave-active {
      transition: 0.3s ease-in all;
    }

    .fade-enter-active {
      transition: 0.3s ease all;
    }

    .fade-leave-active {
      transition: 0.2s ease all;
      transition-delay: 0.3s;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .slide-in-enter-active,
    .slide-in-leave-active,
    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0.01s linear;
    }

    .slide-in-enter-from,
    .slide-in-leave-to {
      transform: none;
    }
  }
</style>
